import throttleUtility from './util.throttle';

interface Offset {
  top: number;
  bottom: number;
}

interface VisibilityParams {
  [key: number]: Element;
}

let instance: VisibilityState | null = null;

class VisibilityState {
  private elements!: NodeListOf<HTMLElement>;
  private visibleEvent!: Event | null;
  private invisibleEvent!: Event | null;
  private defaultOffset!: Offset;

  constructor() {
    if (!instance) {
      instance = this;
      this.elements = document.querySelectorAll('[data-visibility]');
      this.visibleEvent = null;
      this.invisibleEvent = null;
      this.defaultOffset = {
        top: 0,
        bottom: 0
      };
      this._init();
    }
    return instance;
  }

  private _init(): void {
    document.addEventListener('DOMContentLoaded', () => {
      this._initEvents();
      this._checkVisibility();

      window.addEventListener(
        'scroll',
        throttleUtility(() => this._checkVisibility(), 500)
      );
    });
  }

  private _initEvents(): void {
    this.visibleEvent = new Event('visible', {
      bubbles: true,
      cancelable: true
    });

    this.invisibleEvent = new Event('invisible', {
      bubbles: true,
      cancelable: true
    });
  }

  private _checkVisibility(): void {
    this.elements.forEach((element) => {
      const dataVisibility = element.getAttribute('data-visibility');

      if (this._isVisible(element) && dataVisibility !== 'visible') {
        this._setVisibility(element, 'visible', this.visibleEvent!);
      } else if (!this._isVisible(element) && dataVisibility !== 'invisible') {
        this._setVisibility(element, 'invisible', this.invisibleEvent!);
      }
    });
  }

  public refresh(params: NodeListOf<Element>): void {
    this.elements = params as NodeListOf<HTMLElement>;
    this.visibleEvent = null;
    this.invisibleEvent = null;

    this._initEvents();
    this._checkVisibility();
  }

  private _isVisible(element: HTMLElement): boolean {
    const screenHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const rect = element.getBoundingClientRect();
    let offset: Offset = this.defaultOffset;

    offset = this._convertPercent(offset, screenHeight);

    return (
      rect.bottom >= offset.top &&
      rect.left >= 0 &&
      rect.top <= screenHeight - offset.bottom
    );
  }

  private _setVisibility(
    element: HTMLElement,
    state: string,
    event: Event
  ): void {
    element.setAttribute('data-visibility', state);
    element.dispatchEvent(event);
  }

  private _convertPercent(offset: Offset, height: number): Offset {
    const topData = String(offset.top).match(/([0-9]+)%/);

    if (topData && topData[1]) {
      offset.top = (height * parseInt(topData[1])) / 100;
    }

    const bottomData = String(offset.bottom).match(/([0-9]+)%/);
    if (bottomData && bottomData[1]) {
      offset.bottom = (height * parseInt(bottomData[1])) / 100;
    }

    return offset;
  }
}

export default VisibilityState;
export const visibilityState = new VisibilityState();
